'use client';

import Link from 'next/link';
import Image from 'next/image';
import { useState, useEffect, Suspense } from 'react';
import { AuthService } from '@/services/auth';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import RedditColor from '../../../public/reddit-color.svg';
import { UserData } from '@/app/profile/page';
import { Button } from '../ui/button';
import { PlusIcon } from 'lucide-react';
import FullLogo from '../../../public/logo.svg';
import LogoIcon from '../../../public/logo-icon.svg';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

export function Navbar() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userData, setUserData] = useState<UserData | undefined>(undefined);
  const router = useRouter();

  useEffect(() => {
    // Set initial state
    const isLoggedIn = AuthService.isLoggedIn() && !AuthService.isAnonymous();
    setIsLoggedIn(isLoggedIn);

    if (isLoggedIn) {
      AuthService.getCurrentUser().then((user) => {
        setUserData(user);
      });
    }

    // Subscribe to auth state changes
    const unsubscribe = AuthService.onAuthStateChange((loggedIn) => {
      const isLoggedIn = loggedIn && !AuthService.isAnonymous();
      setIsLoggedIn(isLoggedIn);

      if (isLoggedIn) {
        AuthService.getCurrentUser().then((user) => {
          setUserData(user);
        });
      }
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, []);

  const handleCreateSidekick = () => {
    if (AuthService.isAnonymous()) {
      toast.error('You must be signed in to create a sidekick');
      return;
    }
    router.push('/new-sidekick');
  };

  return (
    <nav className="border-b bg-[#0f0f0f]/50 border-border/40 backdrop-blur-lg fixed w-full z-10">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="grid grid-cols-[1fr_auto_1fr] gap-4 md:gap-6 h-16 items-center">
          <div className="flex items-center">
            <Link href="/" prefetch={false}>
              <FullLogo className="w-33 cursor-pointer hidden md:block" />
              <LogoIcon className="h-7 w-auto cursor-pointer md:hidden" />
            </Link>
          </div>
          <a
            href="https://www.reddit.com/r/Sidekick_AI/"
            target="_blank"
            className="flex flex-row h-9 w-fit place-self-center justify-center items-center rounded-full border-2 border-white/10 hover:border-white/15 transition-colors"
          >
            <RedditColor className="size-8" />
            <span className="text-sm inline-flex ps-2 pe-3">Join Reddit →</span>
          </a>

          <div className="flex items-center gap-4 md:gap-6 justify-self-end">
            <Button
              onClick={handleCreateSidekick}
              className="text-sm font-normal bg-transparent hover:bg-transparent border-2 border-white/10 md:border-white/0 hover:border-white/15 transition-colors rounded-full"
            >
              <PlusIcon className="size-4" />
              <span className="hidden md:block">Create Sidekick</span>
            </Button>
            {/* <Link
              href="/chat-history"
              className="text-sm font-normal flex flex-row h-9 justify-center items-center rounded-full border-2 border-white/10 hover:border-white/15 transition-colors px-3 min-w-20"
            >
              Chat History
            </Link> */}
            {isLoggedIn && (
              <Link href="/profile" prefetch={false}>
                <Avatar>
                  <AvatarImage
                    src={`${process.env.NEXT_PUBLIC_API_BASE_URL}${userData?.avatar_url}`}
                  />
                  <AvatarFallback>
                    {userData?.username?.charAt(0).toUpperCase()}
                    {userData?.username?.charAt(1).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
              </Link>
            )}
            {!isLoggedIn && (
              <Link
                href="/login"
                prefetch={false}
                className="text-sm font-normal flex flex-row h-9 justify-center items-center rounded-full border-2 border-white/10 hover:border-white/15 transition-colors px-3 min-w-20"
              >
                Login
              </Link>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}
